import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaPaperPlane, FaGlobeAmericas, 
  FaChevronRight, FaCheckCircle, FaStar, FaChevronDown, 
  FaTimesCircle, FaClock, FaBus, FaHotel, FaUtensils, FaShieldAlt,
  FaCloud, FaWater
} from 'react-icons/fa';
import './MysticalMeghalaya.css';

const MysticalMeghalaya = () => {
  const [openSection, setOpenSection] = useState(null);

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({ top: ref.current.offsetTop - 100, behavior: 'smooth' });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  const itineraryData = [
    {
      day: 1,
      title: "Arrival in Guwahati & Transfer to Shillong",
      points: [
        "Arrival at Guwahati Airport / Railway Station and meet our representative.",
        "Begin the scenic 100 km drive to Shillong (approx. 3 hours).",
        "En-route stop at Umiam Lake (Barapani Lake) for photography and views.",
        "Hotel check-in and evening leisure time.",
        "Dinner and overnight stay in Shillong."
      ],
      icon: <FaBus />
    },
    {
      day: 2,
      title: "Shillong Local Sightseeing & Laitlum Canyon",
      points: [
        "Visit Shillong Peak for panoramic hill views of the city.",
        "Explore Elephant Falls (accessible via 200 steps) and Ward’s Lake.",
        "Drive to Laitlum Grand Canyon for dramatic cliff and valley landscapes.",
        "Evening: Explore Police Bazaar for local cafés, street food, and handicrafts.",
        "Dinner and overnight stay in Shillong."
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 3,
      title: "Shillong to Cherrapunji (Sohra)",
      points: [
        "Drive to Cherrapunji with a stop at Mawkdok Dympep Valley Viewpoint.",
        "Visit Nohkalikai Falls (tallest plunge waterfall) and Seven Sisters Waterfall.",
        "Explore Mawsmai Limestone Caves and Arwah Cave (famous for fossils).",
        "Hotel check-in in Cherrapunji.",
        "Dinner and overnight stay in Cherrapunji."
      ],
      icon: <FaCloud />
    },
    {
      day: 4,
      title: "Double Decker Living Root Bridge Trek",
      points: [
        "Drive to Tyrna Village, the starting point for the Nongriat trek.",
        "Trek 3 km (approx. 3,000 steps) down to the valley.",
        "Explore the unique Double Decker Living Root Bridge and natural pools.",
        "Trek back to Tyrna and return to Cherrapunji.",
        "Dinner and overnight stay in Cherrapunji."
      ],
      icon: <FaGlobeAmericas />
    },
    {
      day: 5,
      title: "Cherrapunji to Dawki & Mawlynnong",
      points: [
        "Drive to Dawki near the India–Bangladesh border.",
        "Enjoy boating on the crystal-clear Umngot River.",
        "Visit Mawlynnong (Asia’s Cleanest Village) and its Living Root Bridge.",
        "Experience the Sky Walk bamboo viewpoint.",
        "Return to Shillong for dinner and overnight stay."
      ],
      icon: <FaWater />
    },
    {
      day: 6,
      title: "Shillong Leisure & Local Exploration",
      points: [
        "Enjoy a relaxed day in the 'Scotland of the East'.",
        "Option to explore local cafés, handicraft shops, and markets.",
        "Take short nature walks around the hill town.",
        "Dinner and overnight stay in Shillong."
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 7,
      title: "Shillong to Guwahati Departure",
      points: [
        "After breakfast, check out and drive back to Guwahati.",
        "Transfer to Guwahati Airport / Railway Station for onward journey.",
        "Tour concludes with unforgettable memories of the Abode of Clouds."
      ],
      icon: <FaCheckCircle />
    }
  ];

  return (
    <div className="trip-container">
      {/* 1. HEADER */}
      <motion.header initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="trip-header">
        <div className="header-left">
          <h1 className="trip-title">Mystical Meghalaya Tour – <br /> Shillong, Cherrapunji, Dawki & Mawlynnong</h1>
          <p className="trip-duration">Duration: 6 Nights & 7 Days of Cloud-Kissed Adventure</p>
        </div>
        <div className="header-right">
          <div className="info-row"><FaMapMarkerAlt className="header-icon" /> <div className="info-text"><span>Destination:</span><p>Shillong, Cherrapunji, Dawki, Mawlynnong</p></div></div>
          <div className="info-row"><FaPaperPlane className="header-icon" /> <div className="info-text"><span>Service Type:</span><p>Private Nature & Adventure Tour</p></div></div>
          <div className="info-row type-tag"><FaCloud className="header-icon" /><p>Abode of Clouds</p></div>
        </div>
      </motion.header>

      {/* 2. IMAGE GRID */}
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="image-grid-container">
        <div className="grid-item large-vertical">
          <img src="https://images.unsplash.com/photo-1608884941702-37653a75a725?q=80&w=1026&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Meghalaya Waterfall" />
        </div>
        <div className="grid-item middle-box">
          <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT43ZP9_lhJWqlIjKK_FnBkyN09tKl0E0dq3Q&s" alt="Double Decker Bridge" />
        </div>
        <div className="right-stack">
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1715939050928-c504fc215456?q=80&w=782&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Umngot River" />
          </div>
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1665248919075-246d0ac9a912?q=80&w=774&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Umiam Lake" />
          </div>
        </div>
      </motion.div>

      {/* 3. STICKY NAV */}
      <nav className="sticky-nav">
        <button className="tab-link" onClick={() => scrollToSection(itineraryRef)}>Detailed Itinerary</button>
        <button className="tab-link" onClick={() => scrollToSection(highlightsRef)}>Highlights</button>
        <button className="tab-link" onClick={() => scrollToSection(inclusionsRef)}>Policy & Inclusions</button>
        <button className="tab-link" onClick={() => scrollToSection(aboutRef)}>About Trip</button>
      </nav>

      <div className="content-section-wrapper">
        {/* ITINERARY SECTION */}
        <section ref={itineraryRef} className="scroll-section">
          <h2 className="section-main-title">Tour Itinerary</h2>
          <div className="days-list">
            {itineraryData.map((item) => (
              <motion.div key={item.day} initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.2 }} variants={fadeInUp} className="day-block">
                <div className="day-header">
                  <span className="day-badge">Day {item.day}</span>
                  <h3>{item.title}</h3>
                </div>
                <div className="day-body">
                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* HIGHLIGHTS SECTION */}
        <motion.section ref={highlightsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Trip Highlights</h2>
          <div className="highlights-grid">
            <div className="h-item"><FaStar className="star-icon" /> <p>Umiam Lake (Barapani) - Picturesque Himalayan Lake</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Laitlum Grand Canyon - Dramatic cliffs & deep valley views</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Nohkalikai Falls - India's tallest plunge waterfall</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Double Decker Living Root Bridge - Nongriat Trek</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Crystal-clear boating experience on Umngot River (Dawki)</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Mawlynnong - Asia's Cleanest Village exploration</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Mawsmai & Arwah Caves - Natural limestone formations</p></div>
          </div>
        </motion.section>

        {/* INCLUSIONS & EXCLUSIONS */}
        <motion.section ref={inclusionsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <div className="policy-box inclusion-box">
            <h4 className="p-title green"><FaCheckCircle /> What's Included</h4>
            <ul className="p-list">
              <li><FaHotel /> Accommodation in comfortable hotels (4N Shillong, 2N Cherrapunji)</li>
              <li><FaClock /> 6 Nights stay on double sharing basis</li>
              <li><FaUtensils /> Meal Plan: Daily Breakfast & Dinner at hotels</li>
              <li><FaBus /> Private vehicle for the entire journey (all sightseeing & transfers)</li>
              <li><FaShieldAlt /> Driver charges, fuel, toll taxes, parking, and permits</li>
              <li><FaPaperPlane /> 24/7 tour coordination and assistance</li>
            </ul>
          </div>
          <div className="policy-box exclusion-box">
            <h4 className="p-title red"><FaTimesCircle /> What's Excluded</h4>
            <ul className="p-list">
              <li><FaTimesCircle /> Airfare or train tickets to/from Guwahati</li>
              <li><FaTimesCircle /> Entry tickets to attractions, caves, or parks</li>
              <li><FaWater /> Boating charges at Dawki (Umngot River)</li>
              <li><FaBus /> Trek guide charges for Nongriat trek</li>
              <li><FaTimesCircle /> Personal expenses (Laundry, Tips, Shopping, etc.)</li>
              <li><FaShieldAlt /> Travel insurance or any medical expenses</li>
              <li><FaTimesCircle /> Additional expenses due to weather or road closures</li>
            </ul>
          </div>
        </motion.section>

        {/* ABOUT SECTION */}
        <motion.section ref={aboutRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Tour Overview</h2>
          <p className="about-text">
            The Mystical Meghalaya Tour offers an unforgettable journey through the breathtaking landscapes of Meghalaya, famously known as the “Abode of Clouds.” This tour explores the region’s most iconic destinations including Shillong, Cherrapunji, Dawki, and Mawlynnong, showcasing dramatic waterfalls, lush valleys, crystal-clear rivers, and unique living root bridges.
            <br /><br />
            Travelers will experience the vibrant hill town of Shillong, the majestic cliffs of Cherrapunji, and the crystal-clear waters of the Umngot River. The journey also includes the thrilling trek to the famous Double Decker Living Root Bridge in Nongriat, one of the most unique natural wonders of Northeast India.
          </p>
        </motion.section>

        {/* FOOTER LINKS */}
        <div className="accordion-item">
          <Link to="/terms-conditions" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
            <span>Terms & Conditions</span>
            <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} /> 
          </Link>
        </div>

        <div className="accordion-item">
          <Link to="/privacy-policy" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
            <span>Privacy Policy</span>
            <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} />
          </Link>
        </div>

        {/* IMPORTANT NOTE */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section travel-info-grid">
          <div className="info-card note-card" style={{ width: '100%' }}>
            <h4 className="info-card-title"><FaPaperPlane /> Important Note</h4>
            <p>Flight and train tickets can be arranged by <strong>Chandrama Holidays</strong> on request at the best available fares. Our team will assist you in planning convenient travel arrangements for a smooth journey.</p>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default MysticalMeghalaya;